import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  useTheme,
  alpha,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  School as SchoolIcon,
  Schedule as ScheduleIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const ServicesHero = ({ isAuthenticated = true, onRequireLogin, onStartApplication }) => {
  const theme = useTheme();
  const highlights = [
    {
      icon: <CheckIcon sx={{ fontSize: 32 }} />,
      title: '98%申请成功率',
      description: '专业团队保障申请质量',
      color: theme.palette.success.main,
    },
    {
      icon: <ScheduleIcon sx={{ fontSize: 32 }} />,
      title: '3-6个月完成',
      description: '高效的申请流程管理',
      color: theme.palette.primary.main,
    },
    {
      icon: <StarIcon sx={{ fontSize: 32 }} />,
      title: '一对一服务',
      description: '专属顾问全程跟进',
      color: theme.palette.secondary.main,
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <Box
      sx={{
        background: `linear-gradient(135deg, 
          ${alpha(theme.palette.primary.main, 0.1)} 0%, 
          ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
        py: 8,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background decorative elements */}
      <Box
        sx={{
          position: 'absolute',
          top: '10%',
          right: '5%',
          width: 150,
          height: 150,
          borderRadius: '50%',
          background: `linear-gradient(45deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.secondary.main, 0.1)})`,
          animation: 'float 6s ease-in-out infinite',
        }}
      />

      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <Grid container spacing={4} alignItems="center">
            {/* Left Content */}
            <Grid item xs={12} lg={6}>
              <motion.div variants={itemVariants}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <SchoolIcon sx={{ fontSize: 40, mr: 2, color: theme.palette.primary.main }} />
                  <Typography
                    variant="h6"
                    sx={{
                      color: theme.palette.primary.main,
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: 1,
                    }}
                  >
                    专业申请服务
                  </Typography>
                </Box>
              </motion.div>

              <motion.div variants={itemVariants}>
                <Typography
                  variant="h1"
                  sx={{
                    mb: 3,
                    fontSize: { xs: '2.5rem', md: '3.5rem' },
                    fontWeight: 700,
                    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    lineHeight: 1.2,
                  }}
                >
                  您的梦想
                  <br />
                  我们来实现
                </Typography>
              </motion.div>

              <motion.div variants={itemVariants}>
                <Typography
                  variant="h5"
                  sx={{
                    mb: 4,
                    color: theme.palette.text.secondary,
                    fontSize: { xs: '1.1rem', md: '1.3rem' },
                    lineHeight: 1.6,
                    maxWidth: 500,
                  }}
                >
                  从专业选择到成功入学，我们提供全程专业指导。
                  15年经验团队，为您定制最适合的留学方案。
                </Typography>
              </motion.div>

              <motion.div variants={itemVariants}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 4 }}>
                  <Button
                    variant="contained"
                    size="large"
                    sx={{
                      px: 4,
                      py: 1.5,
                      borderRadius: 25,
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`,
                      '&:hover': {
                        boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.4)}`,
                        transform: 'translateY(-2px)',
                      },
                    }}
                    onClick={() => {
                      if (!isAuthenticated) {
                        onRequireLogin && onRequireLogin();
                        return;
                      }
                      // 登录后自动滚动到流程区域
                      const el = document.getElementById('application-process');
                      if (el) {
                        el.scrollIntoView({ behavior: 'smooth' });
                      }
                    }}
                  >
                    开始申请
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    sx={{
                      px: 4,
                      py: 1.5,
                      borderRadius: 25,
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      borderWidth: 2,
                      '&:hover': {
                        borderWidth: 2,
                        transform: 'translateY(-2px)',
                      },
                    }}
                    onClick={() => {
                      const el = document.getElementById('application-process-steps');
                      if (el) {
                        el.scrollIntoView({ behavior: 'smooth' });
                      }
                    }}
                  >
                    了解流程
                  </Button>
                </Box>
              </motion.div>

              <motion.div variants={itemVariants}>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontSize: '0.9rem',
                  }}
                >
                  💡 免费咨询评估 | 🔒 信息安全保护 | ⚡ 快速响应服务
                </Typography>
              </motion.div>
            </Grid>

            {/* Right Content - Highlights */}
            <Grid item xs={12} lg={6}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {highlights.map((highlight, index) => (
                  <motion.div
                    key={index}
                    variants={itemVariants}
                    custom={index}
                  >
                    <Card
                      elevation={0}
                      sx={{
                        p: 3,
                        background: alpha(theme.palette.background.paper, 0.8),
                        backdropFilter: 'blur(10px)',
                        border: `1px solid ${alpha(highlight.color, 0.2)}`,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateX(10px)',
                          borderColor: alpha(highlight.color, 0.4),
                          boxShadow: `0 8px 32px ${alpha(highlight.color, 0.15)}`,
                        },
                      }}
                    >
                      <CardContent sx={{ display: 'flex', alignItems: 'center', p: 0 }}>
                        <Box
                          sx={{
                            mr: 3,
                            p: 2,
                            borderRadius: 2,
                            backgroundColor: alpha(highlight.color, 0.1),
                            color: highlight.color,
                          }}
                        >
                          {highlight.icon}
                        </Box>
                        <Box>
                          <Typography
                            variant="h5"
                            sx={{
                              fontWeight: 700,
                              color: theme.palette.text.primary,
                              mb: 0.5,
                            }}
                          >
                            {highlight.title}
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{ color: theme.palette.text.secondary }}
                          >
                            {highlight.description}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </Box>
            </Grid>
          </Grid>
        </motion.div>
      </Container>

      {/* CSS for animations */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-15px); }
        }
      `}</style>
    </Box>
  );
};

export default ServicesHero;
